import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  AccountService,
  AuthService,
  CollectionsService,
  ContentService,
  ContextService,
  DEFAULT_COLLECTIONS_DETAILS,
  FilesService,
  AnalyticsService,
  MetaService,
  PlatformService,
  AuthModalOptions,
  BillingV2Service,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { loadMe } from '@store/actions/profile.actions';
import { loadUserById } from '@store/actions/users.actions';
import { getUserById } from '@store/reducers/users.reducer';
import {
  BillingOperation,
  Collection,
  CollectionAccessEnum,
  CollectionItem,
  CollectionLicenseEnum,
  Content,
  ContentTypeEnum,
  EContentPanelRoute,
  IAccountStats,
  ImageSizeEnum,
  ModerationStatusEnum,
  Router as BaseRouter,
  SubjectCategoryEnum,
  User,
  UserRoleEnum,
  UserSegmentEnum,
} from 'lingo2-models';
import { uniqBy } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover, OnUiTab, PopoverPositionEnum } from 'onclass-ui';
import { Observable } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { RESPONSE } from '../../../../express.tokens';

@Component({
  selector: 'app-collection-card',
  templateUrl: './collection-card.component.html',
  styleUrls: ['./collection-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionCardComponent extends ChangableComponent implements OnInit, OnDestroy {
  @Input() public set collection(collection: Collection) {
    this.showSkeleton = true;
    this._collection = collection;
    if (collection.cover && collection.multi_cover && collection.price_tiers) {
      this.prepareCard();
    } else {
      this.loadCollection();
    }
  }
  public get collection(): Collection {
    return this._collection;
  }
  public get purchasedLicense(): CollectionLicenseEnum {
    return this._collection.visit_info?.licensed;
  }
  @Input() public meta = true;
  @Input() public modal = false;
  @Output() public isCloseAllModals = new EventEmitter<boolean>();

  public debug$ = this.contextService.debug$;
  public fullTitle: string;
  public title: string;
  public subject: string;
  public excerpt: string;
  public description: string;
  public discount: number;
  public items: CollectionItem[];
  public sortedItems: CollectionItem[];
  public selectedLicense: CollectionLicenseEnum;
  public betterLicense: CollectionLicenseEnum;
  public authorName: string;
  public authorAvatarUrl: string;
  public collectionDate: Date;
  public accountRoute: string[];
  public authorAvatar: OnUiCover;
  public stats: Partial<IAccountStats>;
  public multiCoverUrls: string[] = [];
  public typeFilters: OnUiTab[] = [];
  public showTypeFilters = false;
  public selectedType: OnUiTab;
  public collectionUrl: string;
  public editCollectionUrl: string;
  public paidContentModal = false;
  public position: PopoverPositionEnum = PopoverPositionEnum.bottom;
  public isDeleteAlertOpened = false;
  public complainFormOpened = false;
  public subjectIsNative = false;
  public purchaseOperation: BillingOperation;
  public showModal = false;
  public openedCardIndex: number = null;
  public item: Partial<Content> = null;
  public reachedPrev = false;
  public reachedNext = false;
  public isDragScrollEnabled: boolean;
  private originalUrl: string;
  public buyCollectionModal = false;
  public loadedUserInfo = false;

  public _collection: Collection;
  public me: User;
  public showSkeleton: boolean;
  private contentRoute = LibraryRouter.contentRouteUniversal;
  private collectionRoute = LibraryRouter.collectionRouteUniversal;

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    private billingV2Service: BillingV2Service,
    private location: Location,
    private router: Router,
    private contextService: ContextService,
    protected collectionsService: CollectionsService,
    private metaService: MetaService,
    @Optional() @Inject(RESPONSE) private response: any,
    public deviceService: DeviceDetectorService,
    private analytics: AnalyticsService,
    private store: Store,
    private authService: AuthService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  public ngOnInit(): void {
    this.contextService.me$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (me) => {
        this.me = me;
        this.suggestLicense();
        this.markForCheck();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        this.store.dispatch(loadMe());
      },
    });

    this.setTimeout(() => {
      this.incrementView();
    }, 2000);
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.metaService.reset();
  }

  public get isAuthor(): boolean {
    const me_id = this.me?.id;
    const author_id = this._collection?.author?.id || this._collection?.author_id;
    return me_id === author_id;
  }

  public get isModerator(): boolean {
    return this.me && AccountService.hasRole(this.me, UserRoleEnum.moderator);
  }

  public get canBuy(): boolean {
    return !this.purchasedLicense;
  }

  public get canUpgrade(): boolean {
    return !this.isAuthor && !!this.purchasedLicense && this.purchasedLicense !== this.betterLicense;
  }

  public get isOnModeration(): boolean {
    return this.collection.moderation_status === ModerationStatusEnum.default;
  }

  public get isDeclined(): boolean {
    return this.collection.moderation_status === ModerationStatusEnum.softDeclined;
  }

  public get isPrivate(): boolean {
    return this.collection.access === CollectionAccessEnum.private;
  }

  public isOpenedContent(item: CollectionItem): boolean {
    return this.isAuthor || !!this.purchasedLicense || item.is_open;
  }

  public getLicenseName(license: CollectionLicenseEnum): string {
    const licenses = {
      [CollectionLicenseEnum.personal]: 'collection-license.personal',
      [CollectionLicenseEnum.professional]: 'collection-license.professional',
      [CollectionLicenseEnum.business]: 'collection-license.business',
    };
    return licenses[license];
  }

  public getFormattedPrice(amount: number, discount?: number): Observable<string> {
    return this.billingV2Service.formatAmount$(amount, discount);
  }

  public coverCssClasses(count: number, index: number): any {
    switch (count) {
      case 1:
        return { narrow: false, short: false };
      case 2:
        return { narrow: false, short: true };
      case 3:
        return index === 0 ? { narrow: false, short: true } : { narrow: true, short: true };
      case 4:
        return { narrow: true, short: true };
      default:
        return { narrow: false, short: false };
    }
  }

  public showLicenseSelector() {
    if (!this.authService.isAuthenticated) {
      this.authService.showAuthModal(() => {}, { view: 'social' } as AuthModalOptions);
      return;
    }
    this.buyCollectionModal = true;
    this.markForCheck();
  }

  public showPaidContentModal(item?: CollectionItem) {
    this.openedCardIndex = this.sortedItems.findIndex((el) => el.id === item.id);
    this.item = item.content;
    this.checkReached();
    this.paidContentModal = this.item && true;
  }

  public closePaidContentModal(isAll?) {
    this.openedCardIndex = null;
    this.item = null;
    this.paidContentModal = false;
    if (isAll) {
      this.isCloseAllModals.emit(true);
    }
  }

  private prepareCard() {
    if (!this._collection.items.length) {
      this.loadCollectionItems(this._collection);
    }
    this.fullTitle = this.collectionsService.collectionFullTitle(this._collection);
    this.collectionUrl = this.collectionRoute(this._collection);
    this.editCollectionUrl = '/content/collections/edit/' + this._collection.id;

    this.title = this._collection?.title;
    this.subject = this._collection?.subject?.title;
    this.excerpt = this._collection?.excerpt;
    this.description = this._collection?.description;
    this.discount = this._collection?.discount;
    this.items = this._collection?.items || [];
    this.subjectIsNative = this._collection?.subject?.category_id === SubjectCategoryEnum.native_language;
    this.authorName = AccountService.getUserFullName(this._collection?.author);
    this.authorAvatarUrl = FilesService.getFileUrlBySize(this._collection?.author?.userpic_id, ImageSizeEnum.sm);
    this.accountRoute = this._collection?.author ? AccountService.accountRoute(this._collection?.author) : null;
    this.authorAvatar = {
      type: 'avatar',
      img: this.authorAvatarUrl,
      title: this.authorName,
      link: this.accountRoute?.join('/'),
      form: 'circle',
    };
    this.collectionDate = this._collection
      ? this._collection.changed_at
        ? new Date(this._collection.changed_at)
        : new Date(this._collection.created_at)
      : new Date();
    this.stats = this._collection?.author?.stats;
    this.typeFilters = this.prepareTypeFilters();
    this.showTypeFilters = this.items.length > 10 && this.typeFilters.length > 1;
    this.selectedType = this.showTypeFilters ? this.typeFilters.find((_mf) => _mf.route === this.router.url) : null;
    if (!this.selectedType) {
      this.selectedType = this.typeFilters[0];
    }
    this.prepareCover();
    this.loadedUserInfo = true;
    this.markForCheck();

    if (this.meta) {
      this.setupMeta();
    }
  }

  protected loadCollection() {
    this.loadedUserInfo = false;
    this.collectionsService
      .getById(this._collection.id, DEFAULT_COLLECTIONS_DETAILS)
      .pipe(
        switchMap((collection) => {
          this._collection = collection;
          this.store.dispatch(loadUserById({ user_id: this.collection.author_id }));
          return this.store.select(getUserById, { user_id: this.collection.author_id });
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (result) => {
          this._collection.author = result;
          this.loadedUserInfo = true;
          this.prepareCard();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.loadedUserInfo = true;
        },
      });
  }

  protected loadCollectionItems(item: Collection) {
    this.collectionsService
      .getItemsById(item.id, ['id', 'content_id', 'is_open', 'sort_order', 'content'])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (items) => {
          this.items = items.results;
          this.showSkeleton = false;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.showSkeleton = false;
          this.detectChanges();
        },
      });
  }

  protected suggestLicense() {
    const price_tiers = this.collection.price_tiers || {};
    let licenseOrder: CollectionLicenseEnum[];
    const isTeacher = AccountService.isAsIfTeacher(this.me);
    const isSchoolOwner = AccountService.hasSegment(this.me, UserSegmentEnum.school_owner);
    if (isSchoolOwner) {
      licenseOrder = [
        CollectionLicenseEnum.business,
        CollectionLicenseEnum.professional,
        CollectionLicenseEnum.personal,
      ];
    } else if (isTeacher) {
      licenseOrder = [
        CollectionLicenseEnum.professional,
        CollectionLicenseEnum.personal,
        CollectionLicenseEnum.business,
      ];
    } else {
      licenseOrder = [
        CollectionLicenseEnum.personal,
        CollectionLicenseEnum.professional,
        CollectionLicenseEnum.business,
      ];
    }
    this.betterLicense = licenseOrder.find((c) => c in price_tiers && price_tiers[c] > 0);
    if (!this.selectedLicense && !!this.purchasedLicense) {
      this.selectedLicense = this.purchasedLicense;
    }

    if (!this.selectedLicense && this.me && this.collection) {
      this.selectedLicense = this.betterLicense;
    }
  }

  public get selectedLicensePrice(): number {
    const price_tiers = this.collection.price_tiers || {};
    return this.selectedLicense ? price_tiers[this.selectedLicense] : null;
  }

  public processPurchase() {
    this.collectionsService
      .purchase(this.collection.id, this.selectedLicense)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (purchaseOperation) => {
          this.purchaseOperation = purchaseOperation;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'PAYMENT-PROBLEM');
        },
      });
  }

  public get filteredItems(): CollectionItem[] {
    this.sortedItems = this.items?.sort((a, b) => {
      const _a = new Date(a.sort_order);
      const _b = new Date(b.sort_order);
      return _b >= _a ? 1 : -1;
    });
    if (this.selectedType && this.selectedType.code === 'all') {
      return this.sortedItems;
    }

    const type = ContentTypeEnum[this.selectedType?.code];
    return this.sortedItems?.filter((_ci) => _ci.content?.type === type);
  }

  public onToggleDeleteAlert() {
    this.isDeleteAlertOpened = !this.isDeleteAlertOpened;
    this.markForCheck();
  }

  public onDeleteCollection() {
    this.collectionsService
      .archive(this._collection.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.router.navigate(['/' + EContentPanelRoute.collections]);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onBookMark() {}

  public openComplainForm() {
    this.complainFormOpened = true;
    this.detectChanges();
  }

  public closeComplainForm() {
    this.complainFormOpened = false;
    this.detectChanges();
  }

  public _coverItem(coverItem?: string): OnUiCover {
    return {
      type: 'cover',
      img: coverItem ? coverItem : null,
      form: 'base',
    };
  }

  private prepareCover() {
    this.multiCoverUrls = [];
    if (this._collection?.multi_cover_id) {
      this._collection?.multi_cover_id
        .map((cover_id) => {
          this.multiCoverUrls.push(FilesService.getFileUrlBySize(cover_id));
        })
        .slice(0, 4);
    }
    this.detectChanges();
  }

  private navigate(tab: OnUiTab) {
    this.selectedType = tab;
    this.location.replaceState(tab.route);
    this.markForCheck();
  }

  private prepareTypeFilters(): OnUiTab[] {
    const allTypesItem: OnUiTab = {
      code: 'all',
      label: 'collections.text.main-tab.all',
      route: this.collectionUrl,
      routeCondition: this.navigate.bind(this),
    };

    const items = uniqBy(
      this.items.map((_ci) => ({
        code: ContentTypeEnum[_ci.content?.type],
        label: ContentService.contentTypeTitle[_ci.content?.type],
        route: this.collectionUrl + '/' + BaseRouter.getNameByContentType(_ci.content?.type),
        routeCondition: this.navigate.bind(this),
      })),
      'code',
    );

    return [allTypesItem, ...items];
  }

  protected get metaImageId(): string {
    const multiCoverUrls: string[] = [];
    if (this.collection?.multi_cover_id) {
      this.collection?.multi_cover_id
        .map((cover_id) => {
          multiCoverUrls.push(cover_id);
        })
        .slice(0, 4);
    }
    return multiCoverUrls[0];
  }

  protected incrementView() {
    this.collectionsService
      .addView(this._collection.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (response.result) {
            this._collection.stats = {
              ...this._collection.stats,
              views_count: response.view_count,
            };
            this.markForCheck();
          }
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onOpenCard(item: CollectionItem) {
    this.openedCardIndex = this.sortedItems.findIndex((el) => el.id === item.id);
    this.item = item.content;
    const url = this.contentRoute(this.item as Content);
    const currentUrl = this.location.path(false);
    if (currentUrl !== url) {
      if (!this.originalUrl) {
        this.originalUrl = currentUrl;
      }
      this.location.replaceState(url);

      // отправка событий в аналитику для имитации навигации по страницам
      this.analytics.pageView(url, currentUrl);
    }
    this.checkReached();
    this.showModal = this.item && true;
  }

  public onCloseCard(isAll?) {
    this.openedCardIndex = null;
    this.item = null;
    if (this.originalUrl) {
      this.location.replaceState(this.originalUrl);
      this.originalUrl = null;
    }
    this.showModal = false;
    if (isAll) {
      this.isCloseAllModals.emit(true);
    }
  }

  public onCloseModal(event, isAll?) {
    if (event) {
      this.onCloseCard(isAll);
    }
  }

  public togglePrev(event?) {
    if (this.openedCardIndex - 1 >= 0) {
      if (this.isOpenedContent(this.sortedItems[this.openedCardIndex - 1])) {
        this.paidContentModal = false;
        this.onOpenCard(this.sortedItems[this.openedCardIndex - 1]);
      } else {
        if (this.originalUrl) {
          this.location.replaceState(this.originalUrl);
          this.originalUrl = null;
        }
        this.showModal = false;
        this.showPaidContentModal(this.sortedItems[this.openedCardIndex - 1]);
      }
    }
    this.checkReached();
    this.detectChanges();
  }

  public toggleNext(event?) {
    if (this.openedCardIndex + 1 <= this.sortedItems.length - 1) {
      if (this.isOpenedContent(this.sortedItems[this.openedCardIndex + 1])) {
        this.paidContentModal = false;
        this.onOpenCard(this.sortedItems[this.openedCardIndex + 1]);
      } else {
        if (this.originalUrl) {
          this.location.replaceState(this.originalUrl);
          this.originalUrl = null;
        }
        this.showModal = false;
        this.showPaidContentModal(this.sortedItems[this.openedCardIndex + 1]);
      }
    }
    this.checkReached();
    this.detectChanges();
  }

  public checkReached() {
    const reachedPrev = this.openedCardIndex <= 0;
    const reachedNext = this.openedCardIndex >= this.sortedItems.length - 1;

    if (!reachedPrev && !reachedNext) {
      this.reachedPrev = this.reachedNext = false;
    } else if (reachedPrev && reachedNext) {
      this.reachedPrev = this.reachedNext = true;
    } else if (reachedPrev) {
      this.reachedPrev = true;
    } else if (reachedNext) {
      this.reachedNext = true;
    }
  }

  public trackByFn(index) {
    return index;
  }

  protected setupMeta() {
    if (!this.collection) {
      //   return;
    }

    if (this.collection.access === CollectionAccessEnum.private || !this.collection.slug) {
      this.metaService.setTitle(['Collection ?? not found']);
      // TODO remove metaService
      if (this.response) {
        this.response.statusCode = 404;
        this.response.statusMessage = 'Page Not Found';
      }
      return;
    }

    this.metaService.setTitle([this.collectionsService.collectionFullTitle(this.collection)]);

    const description: string[] = [];
    if ((this.collection.excerpt || '').length) {
      description.push(this.collection.excerpt);
    }

    if ((this.collection.description || '').length) {
      description.push(this.collection.description);
    }
    this.metaService.setTypeArticle();

    this.metaService.setDescription(description.join('. '));

    const contentRoute = `/collections/${this.collection.slug}`;
    this.metaService.setUrl(contentRoute);

    const image = this.metaImageId;
    if (image) {
      this.metaService.setImage(image, '');
    }
  }

  public closeBuyCollectionModal() {
    this.buyCollectionModal = false;
  }
}
